import { useCallback } from 'react';
import type { Field } from 'ts-force';
import { useSweepApi } from '../sweep/useSweepApi';
import buildURLQuery from '../../lib/buildURLQuery';
import { useDispatch } from 'react-redux';
import { populateFieldsInfoFromSweepFields } from '../../sweep-fields/sweepFieldsInfoReducer';
import type { CreateFieldFieldInterfaceDto, EditFieldFieldInterfaceDto } from '../../types/fields';
import type { SFFieldInterface } from '../../types/fields';

export type GetSweepFieldsIdsByNamesResponse = {
  fieldIds: string[][];
};

export type SFFieldWithReadOnly = Field & { readVersion?: ReadOnlyField };

export interface SweepFieldsDto {
  objectType: string;
  fields: SFFieldInterface[];
  references?: SweepFieldObjectReferenceDto[];
  queryFields?: string[];
}

export interface SweepFieldsDtoResponse {
  complete: boolean;
  sweepFields: SweepFieldsDto[];
}

export enum FieldTypeGroup {
  Text = 'text',
  Checkbox = 'checkbox',
  Number = 'number',
  Date = 'date',
  Time = 'time',
  DateTime = 'dateTime',
  FieldReference = 'fieldReference',
  UserFieldReference = 'userFieldReference',
  Email = 'email',
}

export interface SweepFieldsGetParams {
  objectType?: string[];
  fieldIds?: string[];
  includeSiblings?: boolean;
  detectLeadingCandidates?: boolean;
  typeGroups?: FieldTypeGroup[];
  crmOrgId?: string;
  includeDescriptionValueSetNameRequired?: boolean;
}

export interface FieldUpdateDto {
  field: EditFieldFieldInterfaceDto;
  enableDeduceValuesOrder?: boolean;
}

export const useSweepFieldsApiFacade = () => {
  const sweepApi = useSweepApi();
  const dispatch = useDispatch();

  return {
    get_sweepFields: useCallback(
      async (query: SweepFieldsGetParams) => {
        const { objectType, fieldIds, typeGroups, ...rest } = query;
        const _objectType = objectType && objectType.join(',');
        const _fieldIds = fieldIds && fieldIds.join(',');
        const _typeGroups = typeGroups && typeGroups.join(',');

        const searchParams = buildURLQuery({
          ...rest,
          objectType: _objectType,
          fieldIds: _fieldIds,
          typeGroups: _typeGroups,
        });
        const { data: sweepFieldsDtoResponse } = await sweepApi.get<SweepFieldsDtoResponse>(
          `/sweep_fields${searchParams ? '?' + searchParams : ''}`,
        );

        dispatch(
          populateFieldsInfoFromSweepFields({
            sweepFields: sweepFieldsDtoResponse.sweepFields.flatMap(
              (sweepFields) => sweepFields.fields,
            ),
          }),
        );
        return sweepFieldsDtoResponse;
      },
      [dispatch, sweepApi],
    ),
    get_sweepField: useCallback(
      async (fieldId: string, crmOrgId?: string, readOnly: boolean = false) => {
        const { data } = await sweepApi.get<{ readVersion?: ReadOnlyField }>(
          `/sweep_fields/${fieldId}?crmOrgId=${crmOrgId}&includeReadOnly=${readOnly}`,
        );
        return data;
      },
      [sweepApi],
    ),
    get_sweepFieldsIdsByNames: useCallback(
      async ({ crmOrgId, fieldNames }: { fieldNames: string[]; crmOrgId: string }) => {
        const searchParams = buildURLQuery({
          fieldNames: fieldNames.join(','),
          crmOrgId,
        });
        const response = await sweepApi.get(`/sweep_fields/ids-by-names?${searchParams}`);
        return response.data as GetSweepFieldsIdsByNamesResponse;
      },
      [sweepApi],
    ),

    post_sweepField: useCallback(
      async ({
        crmOrgId,
        field,
        draft,
      }: {
        crmOrgId: string;
        field: CreateFieldFieldInterfaceDto;
        draft?: boolean;
      }) => {
        const { data: sweepField } = await sweepApi.post<SFFieldInterface>(
          `/sweep_fields?${buildURLQuery({ crmOrgId, draft })}`,
          field,
        );
        dispatch(
          populateFieldsInfoFromSweepFields({
            sweepFields: [sweepField],
          }),
        );
        return sweepField;
      },
      [dispatch, sweepApi],
    ),

    put_sweepField: useCallback(
      async ({ crmOrgId, fieldUpdate }: { crmOrgId?: string; fieldUpdate: FieldUpdateDto }) => {
        const query = buildURLQuery({ crmOrgId });
        const { data: sweepField } = await sweepApi.put<SFFieldInterface>(
          `/sweep_fields/${fieldUpdate.field.id}?${query}`,
          fieldUpdate,
        );

        dispatch(
          populateFieldsInfoFromSweepFields({
            sweepFields: [sweepField],
          }),
        );

        return sweepField;
      },
      [dispatch, sweepApi],
    ),
    delete_sweepField: useCallback(
      async ({ field }: { field: SweepField }) => {
        await sweepApi.delete(`/sweep_fields`, { data: field });
      },
      [sweepApi],
    ),
    post_refreshSweepFieldValueSet: useCallback(
      async ({
        fieldId,
        crmOrgId,
        refreshFromCrmOrgId,
      }: {
        fieldId: string;
        crmOrgId: string;
        refreshFromCrmOrgId: string;
      }) => {
        const query = buildURLQuery({
          crmOrgId,
          refreshFromCrmOrgId,
        });

        return await sweepApi.post(`/sweep_fields/${fieldId}/refresh?${query}`);
      },
      [sweepApi],
    ),
  };
};
